import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { Sort } from '@angular/material/sort';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { UiMoleculeMatTableComponent } from '@shared/components/molecules/ui-molecule-mat-table/ui-molecule-mat-table.component';
import { TableColumn } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-column';
import { TableAction } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-action';
import { ActionEvent } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/action-event';
import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomGlobalSearchComponent } from '@shared/components/atoms/ui-atom-global-search/ui-atom-global-search.component';
import { UiMoleculeDialogDeleteWithConfirmComponent } from '@shared/components/molecules/ui-molecule-dialog-delete-with-confirm/ui-molecule-dialog-delete-with-confirm.component';
import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';
import {
  DialogAdminUpsertService,
  AdminDialogResult,
} from '@shared/services/dialog-admin-upsert.service';
import {
  UserService,
  UserCreate,
  UserUpdate,
  UserListParams,
} from '@core/services/use-cases/user.service';
import { FormService, FormListParams } from '@core/services/use-cases/form.service';
import { DialogFormDashboardService } from '@shared/services/dialog-form-dashboard.service';
import { DialogExportColumnsService } from '@shared/services/dialog-export-columns.service';
import {
  DialogExportProgressService,
  ActiveExportDialog,
} from '@shared/services/dialog-export-progress.service';
import { ExcelExportService } from '@core/services/utils/excel-export.service';
import { FormResponsesExportService } from '@core/services/utils/form-responses-export.service';
import { UiMoleculeDialogAdminUpsertComponent } from '@shared/components/molecules/ui-molecule-dialog-admin-upsert/ui-molecule-dialog-admin-upsert.component';
import {
  FormUpsertDialogComponent,
  FormDialogResult,
} from '@shared/components/molecules/ui-molecule-dialog-form-upsert/ui-molecule-dialog-form-upsert.component';
import {
  FormResponseDialogComponent,
  FormResponseData,
} from '@shared/components/molecules/ui-molecule-dialog-form-response/ui-molecule-dialog-form-response.component';
import {
  BeneficiaryUpsertDialogComponent,
  BeneficiaryDialogResult,
} from '@shared/components/molecules/ui-molecule-dialog-beneficiary-upsert/ui-molecule-dialog-beneficiary-upsert.component';

import {
  Beneficiary,
  BeneficiaryDisplay,
  BeneficiaryCreate,
} from '@core/entities/models/beneficiary.model';
import {
  BeneficiaryService,
  BeneficiaryListParams,
} from '@core/services/use-cases/beneficiary.service';
import { CountryService } from '@core/services/use-cases/country.service';
import { PartnerService } from '@core/services/use-cases/partner.service';
import { AppRoutes } from '@core/constants/app.routes';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole, hasPermission } from '@core/entities/models/role.model';
import { ActivatedRoute } from '@angular/router';
import { SnackbarService } from '@shared/services/snackbar.service';
import { BreadcrumbService } from '@shared/services/breadcrumb.service';
import { formatDate } from '@core/utils/date.utils';
import {
  UiOrganismTitleLogosComponent
} from '@shared/components/organisms/ui-organism-title-logos/ui-organism-title-logos.component';

@Component({
  selector: 'app-beneficiaries-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    UiMoleculeMatTableComponent,
    UiAtomButtonComponent,
    MatDialogModule,
    TranslatePipe,
    UiOrganismMainNavComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    UiMoleculeDialogAdminUpsertComponent,
    FormUpsertDialogComponent,
    BeneficiaryUpsertDialogComponent,
    UiAtomGlobalSearchComponent,
    UiOrganismTitleLogosComponent,
  ],
  templateUrl: './beneficiaries-list.html',
  styleUrl: './beneficiaries-list.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BeneficiariesList implements OnInit {
  // ---------------------------------------------------------------------------
  // Referencias a las tablas
  // ---------------------------------------------------------------------------
  @ViewChildren(UiMoleculeMatTableComponent) tables!: QueryList<UiMoleculeMatTableComponent>;

  // ---------------------------------------------------------------------------
  // Estado de la tabla
  // ---------------------------------------------------------------------------

  /** Datos que alimentan la tabla (con datos transformados para visualización) */
  protected tableData: BeneficiaryDisplay[] = [];

  /** Definición de columnas para ui-molecule-mat-table */
  protected tableColumns: TableColumn[] = [];

  // Título dinámico según nivel jerárquico
  protected pageTitle = ''; // Nivel 3/4: [Country] - [Partner]

  /** Acciones de la tabla (iconos + evento que emite ui-molecule-mat-table) */
  protected readonly tableActions: TableAction[] = [
    {
      icon: 'edit',
      tooltip: 'Edit beneficiary',
      tooltipKey: 'beneficiaries.list.table.actions.edit',
      event: 'edit',
    },
    {
      icon: 'download',
      tooltip: 'Download GDPR consent',
      tooltipKey: 'beneficiaries.list.table.actions.downloadGdpr',
      event: 'download-gdpr',
    },
    {
      icon: 'delete',
      tooltip: 'Delete beneficiary',
      tooltipKey: 'beneficiaries.list.table.actions.delete',
      event: 'delete',
    },
    {
      icon: 'description',
      tooltip: 'Export to Excel',
      tooltipKey: 'beneficiaries.list.actions.export',
      event: 'export-excel',
    },
    {
      icon: 'assignment',
      tooltip: 'View forms',
      tooltipKey: 'beneficiaries.list.actions.forms',
      event: 'view-forms',
    },
  ];

  /** Estado de paginación (modo servidor) */
  protected pageIndex = 0;
  protected pageSize = 10;
  protected totalItems = 0;
  protected userPageIndex = 0;
  protected userPageSize = 10;
  protected userTotalItems = 0;
  protected selectedTab = 0;
  protected isLoading = false;
  protected isLoadingUsers = false;
  protected isLoadingForms = false;

  // Ordenación servidor - Beneficiaries
  protected beneficiarySortBy: BeneficiaryListParams['sortBy'] = undefined;
  protected beneficiarySortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected beneficiaryFilter: string | undefined = undefined;

  // Ordenación servidor - Users
  protected userSortBy: UserListParams['sortBy'] = undefined;
  protected userSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected userFilter: string | undefined = undefined;

  // Ordenación servidor - Forms
  protected formsSortBy: FormListParams['sortBy'] = undefined;
  protected formsSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected formsFilter: string | undefined = undefined;

  /** Datos de usuarios admin_beneficiaries */
  protected usersData: any[] = [];
  protected userColumns: TableColumn[] = [];

  /** Datos de forms del partner */
  protected formsData: any[] = [];
  protected formsColumns: TableColumn[] = [];
  protected formsPageIndex = 0;
  protected formsPageSize = 10;
  protected formsTotalItems = 0;
  private currentPartnerId: string | undefined;

  /** Diálogo de progreso activo durante una exportación de respuestas. */
  private activeExportDialog?: ActiveExportDialog;

  /** Acciones de la tabla de usuarios */
  protected readonly userActions: TableAction[] = [
    {
      icon: 'person',
      tooltip: 'Deactivate user',
      tooltipKey: 'users.list.table.actions.deactivate',
      event: 'toggle-user-status',
      showIf: (user: any) => user.status === 'ACTIVE',
    },
    {
      icon: 'person_off',
      tooltip: 'Activate user',
      tooltipKey: 'users.list.table.actions.activate',
      event: 'toggle-user-status',
      showIf: (user: any) => user.status === 'BLOCKED',
    },
    {
      icon: 'edit',
      tooltip: 'Edit user',
      tooltipKey: 'users.list.table.actions.edit',
      event: 'edit-user',
    },
    {
      icon: 'delete',
      tooltip: 'Delete user',
      tooltipKey: 'users.list.table.actions.delete',
      event: 'delete-user',
    },
  ];

  /** Acciones de la tabla de forms */
  protected readonly formsActions: TableAction[] = [
    {
      icon: 'rocket',
      tooltip: 'Publish',
      tooltipKey: 'forms.list.table.actions.publish',
      event: 'publish-form',
      showIf: (form: any) => {
        // Cohete si publication es null: DRAFT → finalize+publish, FINAL → solo publish
        return form.publication === null && this.canPublish();
      },
    },
    {
      icon: 'edit',
      tooltip: 'Edit form',
      tooltipKey: 'forms.list.table.actions.edit',
      event: 'edit-form',
      showIf: (form: any) => form.status === 'DRAFT',
    },
    {
      icon: 'delete',
      tooltip: 'Delete form',
      tooltipKey: 'forms.list.table.actions.delete',
      event: 'delete-form',
      showIf: (form: any) => form.status === 'DRAFT',
    },
    {
      icon: 'link',
      tooltip: 'Copy link',
      tooltipKey: 'forms.list.table.actions.copyLink',
      event: 'copy-link',
      showIf: (form: any) => form.status === 'FINAL' && form.publication !== null,
    },
    {
      icon: 'space_dashboard',
      tooltip: 'View dashboard',
      tooltipKey: 'forms.list.table.actions.dashboard',
      event: 'dashboard-form',
      disabledIf: (form: any) => {
        // Sin publicación → 0 respuestas garantizado. Si está publicado y la
        // API trae el contador, deshabilitar cuando es 0. Si no trae contador
        // (no debería en este endpoint pero defensivo), dejar habilitado.
        if (!form.publication) return true;
        const total = form.publication.submissions?.total;
        return total !== undefined && total === 0;
      },
    },
    {
      // `file_save` pertenece a Material Symbols (no a la fuente clásica), por eso el fontSet.
      icon: 'file_save',
      iconFontSet: 'material-symbols-outlined',
      tooltip: 'Download responses',
      tooltipKey: 'forms.list.table.actions.downloadResponses',
      event: 'download-responses',
      // `beneficiaries` = publication.submissions.total (ver loadForms). 0 → deshabilitado.
      disabledIf: (form: any) => (form.beneficiaries ?? 0) === 0,
    },
  ];

  /** Permisos del usuario actual */
  protected canCreateBeneficiaries = false;
  protected canEditBeneficiaries = false;
  protected canDeleteBeneficiaries = false;

  /** Filtro de búsqueda global */
  protected globalFilter?: (row: Beneficiary) => boolean;

  // ---------------------------------------------------------------------------
  // Constructor e inyección de dependencias
  // ---------------------------------------------------------------------------

  constructor(
    private readonly beneficiaryService: BeneficiaryService,
    private readonly countryService: CountryService,
    private readonly partnerService: PartnerService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly sessionService: SessionService,
    private readonly route: ActivatedRoute,
    private readonly formService: FormService,
    private readonly dialogAdminUpsert: DialogAdminUpsertService,
    private readonly translateService: TranslateService,
    private readonly snackbar: SnackbarService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly dashboardDialog: DialogFormDashboardService,
    private readonly dialogExportColumns: DialogExportColumnsService,
    private readonly dialogExportProgress: DialogExportProgressService,
    private readonly excelExport: ExcelExportService,
    private readonly responsesExport: FormResponsesExportService,
  ) {}

  // ---------------------------------------------------------------------------
  // Ciclo de vida
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.loadPermissions();
    this.buildTableColumns();
    this.buildUserColumns();
    this.buildFormsColumns();
    // Solo cargar la primera tab inicialmente
    this.loadTable();
    this.setPageTitle();
  }

  /**
   * Verifica si el usuario actual puede publicar (SUPERADMIN o COUNTRY_ADMIN)
   */
  private canPublish(): boolean {
    const currentUser = this.sessionService.getCurrentUser();
    return (
      currentUser?.role === UserRole.ADMIN ||
      currentUser?.role === UserRole.COUNTRY_ADMIN ||
      currentUser?.role === UserRole.PARTNER_USER
    );
  }

  private async setPageTitle(): Promise<void> {
    const countryId = this.route.snapshot.paramMap.get('countryId');
    const partnerId = this.route.snapshot.paramMap.get('partnerId');

    if (countryId && partnerId) {
      const [countryName, partnerName] = await Promise.all([
        this.breadcrumbService.getCountryName(countryId),
        this.breadcrumbService.getPartnerName(partnerId),
      ]);
      this.pageTitle = `${countryName} - ${partnerName}`;
    } else if (partnerId) {
      const partnerName = await this.breadcrumbService.getPartnerName(partnerId);
      this.pageTitle = partnerName;
    } else {
      this.pageTitle = 'Beneficiaries';
    }
    this.cdr.markForCheck();
  }

  /**
   * Carga los permisos del usuario actual.
   */
  private loadPermissions(): void {
    const currentUser = this.sessionService.getCurrentUser();
    if (!currentUser) return;

    const role = currentUser.role;
    this.canCreateBeneficiaries = hasPermission(role, 'canCreateBeneficiaries');
    this.canEditBeneficiaries = hasPermission(role, 'canEditBeneficiaries');
    this.canDeleteBeneficiaries = hasPermission(role, 'canDeleteBeneficiaries');

    // Actualizar acciones de la tabla según permisos
    this.updateTableActions();
  }

  /**
   * Actualiza las acciones disponibles en la tabla según permisos.
   */
  private updateTableActions(): void {
    const actions: TableAction[] = [];

    // Edit: Solo PARTNER_USER puede editar sus beneficiarios
    if (this.canEditBeneficiaries) {
      actions.push({
        icon: 'edit',
        tooltip: 'Edit beneficiary',
        tooltipKey: 'beneficiaries.list.table.actions.edit',
        event: 'edit',
      });
    }

    // Upload GDPR: Todos pueden subir
    actions.push({
      icon: 'upload_file',
      tooltip: 'Upload GDPR consent',
      tooltipKey: 'beneficiaries.list.table.actions.uploadGdpr',
      event: 'upload-gdpr',
    });

    // Download GDPR: Deshabilitado si no tiene GDPR
    actions.push({
      icon: 'download',
      tooltip: 'Download GDPR consent',
      tooltipKey: 'beneficiaries.list.table.actions.downloadGdpr',
      event: 'download-gdpr',
      disabledIf: (row: any) => !row.gdpr,
    });

    // Delete: Solo PARTNER_USER puede eliminar sus beneficiarios
    if (this.canDeleteBeneficiaries) {
      actions.push({
        icon: 'delete',
        tooltip: 'Delete beneficiary',
        tooltipKey: 'beneficiaries.list.table.actions.delete',
        event: 'delete',
      });
    }

    // Reemplazar las acciones de la tabla
    (this.tableActions as TableAction[]) = actions;
  }

  // ---------------------------------------------------------------------------
  // Carga / refresco de datos
  // ---------------------------------------------------------------------------

  /**
   * Configura las columnas de la tabla.
   * Usamos titleKey y placeholderKey para que el componente ui-molecule-mat-table
   * resuelva las traducciones en tiempo real via translate pipe.
   */
  private buildTableColumns(): void {
    this.tableColumns = [
      {
        key: 'id',
        title: 'ID',
        titleKey: 'beneficiaries.list.table.columns.id',
        type: 'copy-text',
        width: '120px',
        sortable: false,
        filterable: false,
        iconTooltipKey: 'beneficiaries.list.table.tooltips.copyId',
        sensitive: false,
      },
      {
        key: 'firstName',
        title: 'First Name',
        titleKey: 'beneficiaries.list.table.columns.firstName',
        type: 'text',
        width: '150px',
        sortable: true,
        filterable: false,
        sensitive: true,
      },
      {
        key: 'lastName',
        title: 'Last Name',
        titleKey: 'beneficiaries.list.table.columns.lastName',
        type: 'text',
        width: '150px',
        sortable: true,
        filterable: false,
        sensitive: true,
      },
      {
        key: 'email',
        title: 'Email',
        titleKey: 'beneficiaries.list.table.columns.email',
        type: 'email',
        sortable: true,
        filterable: false,
        sensitive: true,
      },
      {
        key: 'phone',
        title: 'Phone',
        titleKey: 'beneficiaries.list.table.columns.phone',
        type: 'phone',
        width: '160px',
        filterable: false,
        sensitive: true,
      },
      {
        key: 'gender',
        title: 'Gender',
        titleKey: 'beneficiaries.list.table.columns.gender',
        type: 'custom',
        width: '160px',
        filterable: false,
        getValue: (row) =>
          row.gender
            ? this.translateService.instant('beneficiaries.form.gender.options.' + row.gender)
            : '',
      },
      {
        key: 'birthDate',
        title: 'Date of Birth',
        titleKey: 'beneficiaries.list.table.columns.birthDate',
        type: 'text',
        width: '130px',
        filterable: false,
        sensitive: true,
      },
      {
        key: 'hostCountry',
        title: 'Host Country',
        titleKey: 'beneficiaries.list.table.columns.hostCountry',
        type: 'text',
        width: '140px',
        filterable: false,
      },
      {
        key: 'birthCountry',
        title: 'Country of Birth',
        titleKey: 'beneficiaries.list.table.columns.countryOfBirth',
        type: 'text',
        width: '150px',
        filterable: false,
        sensitive: true,
      },
      {
        key: 'externalId',
        title: 'External ID',
        titleKey: 'beneficiaries.list.table.columns.externalId',
        type: 'text',
        width: '150px',
        filterable: false,
        excludeFromDefaultExport: true,
      },
    ];
  }

  /**
   * Carga todos los beneficiarios desde el servicio (localStorage por ahora)
   * y actualiza la tabla.
   */
  private loadTable(): void {
    this.isLoading = true;

    const countryId = this.route.snapshot.paramMap.get('countryId');
    const partnerId = this.route.snapshot.paramMap.get('partnerId');

    const beneficiaryParams: BeneficiaryListParams = {
      offset: this.pageIndex * this.pageSize,
      limit: this.pageSize,
    };

    if (partnerId) {
      beneficiaryParams.partnerId = partnerId;
    }
    if (countryId) {
      beneficiaryParams.countryId = countryId;
    }

    if (this.beneficiarySortBy) {
      beneficiaryParams.sortBy = this.beneficiarySortBy;
    }
    if (this.beneficiarySortOrder) {
      beneficiaryParams.sortOrder = this.beneficiarySortOrder;
    }

    if (this.beneficiaryFilter) {
      beneficiaryParams.filter = this.beneficiaryFilter;
    }

    this.beneficiaryService.getAllForDisplay(beneficiaryParams).subscribe({
      next: (response) => {
        this.tableData = response.items;
        this.totalItems = response.total;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.snackbar.notifyHttpError(err);
        this.tableData = [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Handlers de eventos de la tabla
  // ---------------------------------------------------------------------------

  /**
   * Se llama cuando el usuario hace clic en una acción de la tabla
   * (edit, download-gdpr, delete...).
   */
  protected onTableAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;
    const row = rowData as Beneficiary;

    switch (event) {
      case 'edit':
        this.onEdit(row);
        break;

      case 'download-gdpr':
        this.onDownloadGdpr(row);
        break;

      case 'upload-gdpr':
        this.onUploadGdpr(row);
        break;

      case 'delete':
        this.onDelete(row);
        break;

      case 'export-excel':
        this.onExportExcel();
        break;

      case 'view-forms':
        this.onViewForms(row);
        break;

      default:
        break;
    }
  }

  /**
   * Click sobre toda la fila de la tabla.
   * Navega al detalle del beneficiario.
   */
  protected onTableRowClick(row: Beneficiary): void {
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const partnerId = params.get('partnerId');

      if (countryId && partnerId) {
        void this.router
          .navigate([
            '/',
            AppRoutes.admin,
            AppRoutes.countries,
            countryId,
            AppRoutes.partners,
            partnerId,
            AppRoutes.beneficiaries,
            row.id,
          ])
          .catch(() => {});
      }
    });
  }

  /**
   * Cambio de página desde el paginador interno de ui-molecule-mat-table.
   * Aquí solo mantenemos el estado local.
   */
  protected onTablePageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadTable();
  }

  protected onTableSortChange(sort: Sort): void {
    const columnToApiField: Record<string, BeneficiaryListParams['sortBy']> = {
      firstName: 'firstName',
      lastName: 'lastName',
      email: 'email',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
    };

    if (sort.direction) {
      this.beneficiarySortBy = columnToApiField[sort.active];
      this.beneficiarySortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.beneficiarySortBy = undefined;
      this.beneficiarySortOrder = undefined;
    }

    this.pageIndex = 0;
    this.loadTable();
  }

  // ---------------------------------------------------------------------------
  // Acciones de alto nivel (edit / fichero GDPR / delete)
  // ---------------------------------------------------------------------------

  /**
   * Acción de edición.
   * Obtiene los datos originales del beneficiario y abre el diálogo de edición.
   */
  private onEdit(row: Beneficiary): void {
    const countryId = this.route.snapshot.paramMap.get('countryId');
    const partnerId = this.route.snapshot.paramMap.get('partnerId');

    // Obtener datos originales del beneficiario (sin transformar) antes de editar
    this.beneficiaryService.getById(row.id).subscribe({
      next: (beneficiary) => {
        const dialogRef = this.dialog.open(BeneficiaryUpsertDialogComponent, {
          width: '90vw',
          maxWidth: '900px',
          maxHeight: '90vh',
          autoFocus: 'first-tabbable',
          panelClass: ['dialog-lg', 'dialog-safe-area'],
          data: {
            title: this.translateService.instant('beneficiaries.form.editTitle'),
            mode: 'edit',
            beneficiary: beneficiary,
            countryId: countryId || undefined,
            partnerId: partnerId || undefined,
          },
        });

        dialogRef.afterClosed().subscribe((result: BeneficiaryDialogResult | undefined) => {
          if (result && result.mode === 'edit' && result.beneficiaryId) {
            this.beneficiaryService.update(result.beneficiaryId, result.payload).subscribe({
              next: () => {
                this.breadcrumbService.invalidateBeneficiaryCache(result.beneficiaryId);
                this.snackbar.open(
                  this.translateService.instant('beneficiaries.list.messages.updateSuccess')
                );
                // Forzar recarga de la tabla y detección de cambios
                this.tableData = [];
                this.cdr.detectChanges();
                this.loadTable();
              },
              error: (error) => {
                console.error('Error updating beneficiary:', error);
                this.snackbar.open(
                  this.translateService.instant('beneficiaries.list.messages.updateError')
                );
              },
            });
          }
        });
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }

  /**
   * Acción de descarga del fichero de consentimiento GDPR del beneficiario.
   */
  private onDownloadGdpr(row: Beneficiary): void {
    this.beneficiaryService.downloadGdpr(row.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Usar nombre genérico sin forzar extensión .pdf
        const fileName = `gdpr_${row.firstName}_${row.lastName}`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.snackbar.open(
          this.translateService.instant('beneficiaries.list.gdpr.downloadError')
        );
      },
    });
  }

  /**
   * Acción de subida del fichero de consentimiento GDPR del beneficiario.
   */
  private onUploadGdpr(row: Beneficiary): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        this.beneficiaryService.uploadGdpr(row.id, file).subscribe({
          next: () => {
            this.snackbar.open(
              this.translateService.instant('beneficiaries.list.gdpr.uploadSuccess')
            );
            this.loadTable();
          },
          error: () => {
            this.snackbar.open(
              this.translateService.instant('beneficiaries.list.gdpr.uploadError')
            );
          },
        });
      }
    };
    input.click();
  }

  /**
   * Acción de borrado → abre el diálogo de confirmación.
   */
  private onDelete(row: Beneficiary): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: {
        itemName: `${row.firstName} ${row.lastName}`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.beneficiaryService.delete(row.id).subscribe({
        next: () => this.loadTable(),
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Crear beneficiario (botón "New beneficiary")
  // ---------------------------------------------------------------------------

  /**
   * Acción para el botón de "crear beneficiario".
   * Abre el diálogo de creación de beneficiario.
   */
  protected goToCreate(): void {
    const countryId = this.route.snapshot.paramMap.get('countryId');
    const partnerId = this.route.snapshot.paramMap.get('partnerId');

    if (!partnerId) {
      return;
    }

    const dialogRef = this.dialog.open(BeneficiaryUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '900px',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
      panelClass: ['dialog-lg', 'dialog-safe-area'],
      data: {
        title: this.translateService.instant('beneficiaries.form.createTitle'),
        mode: 'create',
        countryId: countryId || undefined,
        partnerId: partnerId,
      },
    });

    dialogRef.afterClosed().subscribe((result: BeneficiaryDialogResult | undefined) => {
      if (result && result.mode === 'create' && result.partnerId) {
        this.beneficiaryService
          .createInPartner(result.partnerId, result.payload as BeneficiaryCreate)
          .subscribe({
            next: () => {
              this.loadTable();
            },
            error: (err) => this.snackbar.notifyHttpError(err),
          });
      }
    });
  }

  /**
   * Acción de exportación a Excel.
   * TODO: Implementar cuando exista endpoint de exportación.
   */
  private onExportExcel(): void {
    alert('Export to Excel not implemented yet - pending API endpoint');
  }

  /**
   * Acción para ver formularios del beneficiario.
   * Navega a la lista de formularios que puede responder esta persona.
   */
  private onViewForms(row: Beneficiary): void {
    // TODO: Implementar navegación a /beneficiaries/:id/forms cuando exista
    alert('View forms not implemented yet - pending forms page');
  }

  // ---------------------------------------------------------------------------
  // Búsqueda global
  // ---------------------------------------------------------------------------

  /**
   * Maneja el evento de búsqueda global.
   * Filtra los datos de la tabla buscando en todas las columnas.
   */
  protected onGlobalSearch(searchTerm: string): void {
    this.beneficiaryFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.pageIndex = 0;
      this.loadTable();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.beneficiaryFilter = undefined;
      this.loadTable();
    }
  }

  private buildUserColumns(): void {
    this.userColumns = [
      {
        key: 'email',
        title: 'Email',
        titleKey: 'users.list.table.columns.email',
        type: 'email',
        width: '220px',
        sortable: true,
        filterable: false,
      },
      {
        key: 'firstName',
        title: 'FirstName',
        titleKey: 'users.list.table.columns.firstName',
        type: 'text',
        sortable: true,
        filterable: false,
      },
      {
        key: 'lastName',
        title: 'LastName',
        titleKey: 'users.list.table.columns.lastName',
        type: 'text',
        sortable: true,
        filterable: false,
      },
      {
        key: 'enabled',
        title: 'Enabled',
        titleKey: 'users.list.table.columns.enabled',
        type: 'text',
        width: '90px',
        filterable: false,
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Users.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'CreatedAt',
      //   titleKey: 'users.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'UpdatedAt',
      //   titleKey: 'users.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
    ];
  }

  private buildFormsColumns(): void {
    this.formsColumns = [
      {
        key: 'name',
        title: 'Name',
        titleKey: 'forms.list.table.columns.name',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'published',
        title: 'Published',
        titleKey: 'forms.list.table.columns.published',
        type: 'text',
        width: '90px',
        filterable: false,
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Forms.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'Created at',
      //   titleKey: 'forms.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'Updated at',
      //   titleKey: 'forms.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      {
        key: 'beneficiaries',
        title: 'Responses',
        titleKey: 'forms.list.table.columns.beneficiaries',
        type: 'text',
        width: '120px',
        sortable: true,
        filterable: false,
      },
    ];
    this.cdr.markForCheck();
  }

  private loadForms(): void {
    this.isLoadingForms = true;
    // Cargar forms reales desde la API (solo PUBLISHED para vista de partner)
    // Obtener partnerId de la ruta actual
    this.route.paramMap.subscribe((params) => {
      const partnerId = params.get('partnerId');

      // Guardar partnerId para uso posterior
      this.currentPartnerId = partnerId || undefined;

      if (partnerId) {
        const formParams: FormListParams = {
          offset: this.formsPageIndex * this.formsPageSize,
          limit: this.formsPageSize,
        };

        if (this.formsFilter && this.formsFilter.length >= 3) {
          formParams.filter = this.formsFilter;
        }
        if (this.formsSortBy) {
          formParams.sortBy = this.formsSortBy;
        }
        if (this.formsSortOrder) {
          formParams.sortOrder = this.formsSortOrder;
        }

        this.formService.getAllInPartner(partnerId, formParams).subscribe({
          next: (response) => {
            // Mapear todos los formularios (el cohete se muestra/oculta según publication)
            this.formsData = response.items.map((form) => {
              const mappedForm = {
                id: form.id,
                name: form.name,
                status: form.status,
                schema: form.schema,
                publication: form.publication,
                partnerId: partnerId, // Usar el partnerId del contexto
                published: form.status === 'FINAL' ? '✓' : '',
                createdAt: formatDate(form.createdAt),
                updatedAt: formatDate(form.updatedAt),
                beneficiaries: (form.publication as any)?.submissions?.total ?? 0,
              };
              return mappedForm;
            });
            this.formsTotalItems = response.total;
            this.isLoadingForms = false;
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.snackbar.notifyHttpError(error);
            this.formsData = [];
            this.isLoadingForms = false;
            this.cdr.markForCheck();
          },
        });
      } else {
        this.isLoadingForms = false;
      }
    });
  }

  private loadUsers(): void {
    this.isLoadingUsers = true;
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const partnerId = params.get('partnerId');

      if (countryId && partnerId) {
        const userParams: UserListParams = {
          offset: this.userPageIndex * this.userPageSize,
          limit: this.userPageSize,
          countryId: countryId,
          partnerId: partnerId,
          role: 'PARTNER_USER',
        };

        if (this.userFilter && this.userFilter.length >= 3) {
          userParams.filter = this.userFilter;
        }
        if (this.userSortBy) {
          userParams.sortBy = this.userSortBy;
        }
        if (this.userSortOrder) {
          userParams.sortOrder = this.userSortOrder;
        }

        this.userService.getAll(userParams).subscribe({
          next: (response) => {
            this.usersData = response.items.map((user) => ({
              id: user.id,
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              enabled: user.status === 'ACTIVE' ? '✓' : '', // Manejar ambos casos
              status: user.status, // ✅ CONSERVAR la propiedad original
              createdAt: formatDate(user.createdAt),
              updatedAt: formatDate(user.updatedAt),
            }));
            this.userTotalItems = response.total;
            this.isLoadingUsers = false;
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.snackbar.notifyHttpError(error);
            this.usersData = [];
            this.isLoadingUsers = false;
            this.cdr.markForCheck();
          },
        });
      } else {
        this.usersData = [];
        this.isLoadingUsers = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected openCreateUserDialog(): void {
    // Obtener los IDs desde la ruta actual
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const partnerId = params.get('partnerId');

      if (countryId && partnerId) {
        // Abrir diálogo para crear Partner Admin (nivel partner)
        this.dialogAdminUpsert
          .open({
            title: 'Create Partner Admin',
            message:
              'Create a new partner administrator with access only to this specific partner.',
            availableRoles: ['PARTNER_USER'],
            context: 'partner',
            countryId: countryId,
            partnerId: partnerId,
            mode: 'create',
          })
          .subscribe((result: AdminDialogResult | undefined) => {
            if (result && result.mode === 'create') {
              this.userService.create(result.payload as UserCreate).subscribe({
                next: () => {
                  // Recargar la lista de usuarios
                  this.loadUsers();
                },
                error: (error) => this.snackbar.notifyHttpError(error),
              });
            }
          });
      } else if (partnerId) {
        // Escenario legacy: solo partnerId
        // TODO: Obtener countryId desde el partner
        this.dialogAdminUpsert
          .open({
            title: 'Create Partner Admin',
            message:
              'Create a new partner administrator with access only to this specific partner.',
            availableRoles: ['PARTNER_USER'],
            context: 'partner',
            countryId: 'temp-country-id', // TODO: Obtener desde API
            partnerId: partnerId,
            mode: 'create',
          })
          .subscribe((result: AdminDialogResult | undefined) => {
            if (result && result.mode === 'create') {
              this.userService.create(result.payload as UserCreate).subscribe({
                next: () => {
                  this.loadUsers();
                },
                error: (error) => this.snackbar.notifyHttpError(error),
              });
            }
          });
      }
    });
  }

  protected onUserAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;

    switch (event) {
      case 'edit-user':
        this.openEditUserDialog(rowData);
        break;
      case 'toggle-user-status':
        this.toggleUserStatus(rowData);
        break;
      case 'delete-user':
        this.openDeleteUserDialog(rowData);
        break;
      default:
        break;
    }
  }

  private openEditUserDialog(user: any): void {
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const partnerId = params.get('partnerId');

      if (countryId && partnerId) {
        this.dialogAdminUpsert
          .open({
            title: 'Edit Partner Admin',
            message: 'Edit the partner administrator details.',
            defaults: {
              firstName: user.firstName || user.name?.split(' ')[0] || '',
              lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
              email: user.email,
              role: user.role,
            },
            availableRoles: ['PARTNER_USER'],
            context: 'partner',
            mode: 'edit',
            user: user,
          })
          .subscribe((result: AdminDialogResult | undefined) => {
            if (result && result.mode === 'edit' && result.userId) {
              this.userService.update(result.userId, result.payload as UserUpdate).subscribe({
                next: () => {
                  this.loadUsers();
                },
                error: (error) => this.snackbar.notifyHttpError(error),
              });
            }
          });
      }
    });
  }

  private openDeleteUserDialog(user: any): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: { itemName: user.name },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.userService.delete(user.id).subscribe({
        next: () => this.loadUsers(),
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    });
  }

  private toggleUserStatus(user: any): void {
    const isCurrentlyActive = user.status === 'ACTIVE';
    const action = isCurrentlyActive ? 'block' : 'unblock';
    const actionText = isCurrentlyActive ? 'blocking' : 'unblocking';

    const serviceCall = isCurrentlyActive
      ? this.userService.block(user.id)
      : this.userService.unblock(user.id);

    serviceCall.subscribe({
      next: (updatedUser) => {
        this.loadUsers(); // Recargar la tabla para ver el estado actualizado
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }

  protected onUserPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.userPageIndex = event.pageIndex;
    this.userPageSize = event.pageSize;
    this.loadUsers();
  }

  protected openCreateFormDialog(): void {
    // Obtener partnerId de la ruta actual (estamos a nivel de partner)
    const partnerId = this.route.snapshot.paramMap.get('partnerId');

    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.createTitle'),
        mode: 'create',
        context: 'partner',
        partnerId: partnerId || undefined,
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'create') {
        this.handleFormCreate(result.payload, result.partnerId);
      }
    });
  }

  private handleFormCreate(payload: any, partnerId?: string): void {
    // Crear form (el partnerId ya está en el payload si fue proporcionado)
    this.formService.create(payload).subscribe({
      next: () => {
        this.snackbar.open(
          this.translateService.instant('forms.list.messages.created')
        );
        this.loadForms(); // Recargar la tabla al final
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }

  protected onUserSortChange(sort: Sort): void {
    const columnToApiField: Record<string, UserListParams['sortBy']> = {
      email: 'email',
      firstName: 'firstName',
      lastName: 'lastName',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
    };

    if (sort.direction) {
      this.userSortBy = columnToApiField[sort.active];
      this.userSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.userSortBy = undefined;
      this.userSortOrder = undefined;
    }

    this.userPageIndex = 0;
    this.loadUsers();
  }

  protected onUserGlobalSearch(searchTerm: string): void {
    this.userFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.userPageIndex = 0;
      this.loadUsers();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.userFilter = undefined;
      this.loadUsers();
    }
  }

  protected onFormPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.formsPageIndex = event.pageIndex;
    this.formsPageSize = event.pageSize;
    this.loadForms();
  }

  protected onFormSortChange(sort: Sort): void {
    const columnToApiField: Record<string, FormListParams['sortBy']> = {
      name: 'name',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
      beneficiaries: 'publication.submissions.total',
    };

    if (sort.direction) {
      this.formsSortBy = columnToApiField[sort.active];
      this.formsSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.formsSortBy = undefined;
      this.formsSortOrder = undefined;
    }

    this.formsPageIndex = 0;
    this.loadForms();
  }

  protected onFormsGlobalSearch(searchTerm: string): void {
    this.formsFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.formsPageIndex = 0;
      this.loadForms();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.formsFilter = undefined;
      this.loadForms();
    }
  }

  protected onFormFilterChange(filters: Record<string, string>): void {
    const filterValue = Object.values(filters).find((v) => v && v.length >= 3);
    if (filterValue) {
      this.formsFilter = filterValue;
      this.formsPageIndex = 0;
      this.loadForms();
    } else if (Object.values(filters).every((v) => !v || v.length === 0)) {
      this.formsFilter = undefined;
      this.loadForms();
    }
  }

  protected onTabChange(index: number): void {
    this.selectedTab = index;

    // Cargar datos según la tab seleccionada (lazy loading)
    switch (index) {
      case 0: // Beneficiaries tab
        if (this.tableData.length === 0) {
          this.loadTable();
        }
        break;
      case 1: // Forms tab
        if (this.formsData.length === 0) {
          this.loadForms();
        }
        break;
      case 2: // Users tab
        if (this.usersData.length === 0) {
          this.loadUsers();
        }
        break;
    }
  }

  protected onFormAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;

    switch (event) {
      case 'copy-link':
        this.copyFormLink(rowData);
        break;
      case 'edit-form':
        this.editForm(rowData);
        break;
      case 'publish-form':
        this.publishFormToPartner(rowData);
        break;
      case 'delete-form':
        this.deleteForm(rowData);
        break;
      case 'dashboard-form':
        this.openDashboardForm(rowData);
        break;
      case 'download-responses':
        this.onDownloadResponses(rowData);
        break;
      default:
        break;
    }
  }

  /**
   * Descarga a Excel todas las respuestas SUBMITTED de los beneficiarios del partner
   * para el formulario indicado. Abre el selector de columnas de beneficiario (sensibles
   * desmarcadas por defecto), agrega las respuestas y genera el XLSX en el web worker.
   */
  private onDownloadResponses(form: any): void {
    const partnerId = this.route.snapshot.paramMap.get('partnerId');
    if (!partnerId) {
      this.snackbar.open(this.translateService.instant('exportProgress.dialog.error'));
      return;
    }

    this.dialogExportColumns
      .open(this.responsesExport.getBeneficiaryExportColumns())
      .subscribe((selectedKeys) => {
        if (!selectedKeys || selectedKeys.length === 0) return;

        this.activeExportDialog = this.dialogExportProgress.open();

        this.formService
          .getAllSubmittedResponsesWithBeneficiaryInPartner(partnerId, form.id)
          .subscribe({
            next: (entries) => {
              if (entries.length === 0) {
                this.activeExportDialog?.close();
                this.activeExportDialog = undefined;
                this.snackbar.open(this.translateService.instant('forms.dashboard.empty'));
                return;
              }

              const { rows, headers } = this.responsesExport.buildSheet(
                form.schema ?? {},
                entries,
                selectedKeys,
              );
              this.runResponsesExport(rows, headers, `${form.name}_responses`);
            },
            error: () => {
              this.activeExportDialog?.close();
              this.activeExportDialog = undefined;
              this.snackbar.open(this.translateService.instant('exportProgress.dialog.error'));
            },
          });
      });
  }

  private runResponsesExport(
    rows: Record<string, unknown>[],
    headers: string[],
    fileName: string,
  ): void {
    if (!this.activeExportDialog) return;
    const { progress$, cancel$, close } = this.activeExportDialog;

    this.excelExport.exportRowsWithWorker(rows, headers, fileName, progress$, cancel$).subscribe({
      next: () => {
        close();
        this.activeExportDialog = undefined;
      },
      error: () => {
        close();
        this.activeExportDialog = undefined;
        this.snackbar.open(this.translateService.instant('exportProgress.dialog.error'));
      },
    });
  }

  private openDashboardForm(form: any): void {
    // Scope por la ruta actual (esta página está bajo /admin/countries/:countryId/partners/:partnerId/beneficiaries,
    // por lo que ambos params suelen existir; partnerId prevalece).
    const partnerId = this.route.snapshot.paramMap.get('partnerId') || undefined;
    const countryId = this.route.snapshot.paramMap.get('countryId') || undefined;

    const responses$ = partnerId
      ? this.formService.getAllSubmittedAnswersForFormInPartner(partnerId, form.id)
      : countryId
        ? this.formService.getAllSubmittedAnswersForFormInCountry(countryId, form.id)
        : this.formService.getAllSubmittedAnswersForForm(form.id);

    const form$ = partnerId
      ? this.formService.getByIdInPartner(partnerId, form.id)
      : countryId
        ? this.formService.getByIdInCountry(countryId, form.id)
        : this.formService.getById(form.id);

    form$.subscribe({
      next: (fullForm) => {
        responses$.subscribe({
          next: (responses) => {
            const scopeKey = partnerId
              ? 'forms.dashboard.scope.partner'
              : countryId
                ? 'forms.dashboard.scope.country'
                : 'forms.dashboard.scope.global';
            const scopeLabel = this.translateService.instant(scopeKey, {
              name: this.pageTitle || '',
            });

            this.dashboardDialog.open({
              formName: fullForm.name,
              schema: fullForm.schema ?? {},
              responses,
              scopeLabel,
            });
          },
          error: () => {
            this.snackbar.open(this.translateService.instant('forms.dashboard.empty'));
          },
        });
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }

  /**
   * Copia el enlace público del formulario al portapapeles usando el token de publicación o formId como fallback
   * En GitHub Pages develop, incluye el baseHref /jrs-forms-web/
   */
  private copyFormLink(form: any): void {
    const baseUrl = window.location.origin;
    const currentPath = window.location.pathname;

    const identifier = form.publication?.token || form.id;

    // Detectar si estamos en GitHub Pages develop con subdirectorio /jrs-forms-web/
    // Solo añadir el prefijo si:
    // 1. Estamos en github.io (GitHub Pages)
    // 2. La URL contiene /jrs-forms-web/ (entorno develop)
    const isGitHubPages = window.location.hostname.includes('github.io');
    const isGitHubPagesDev = isGitHubPages && currentPath.startsWith('/jrs-forms-web/');
    const basePath = isGitHubPagesDev ? '/jrs-forms-web' : '';
    const url = `${baseUrl}${basePath}/#/public/form/${identifier}`;

    if (!form.publication?.token) {
      console.warn('⚠️ Using formId as fallback (token not available yet)');
    }

    navigator.clipboard.writeText(url).then(() => {
      this.snackbar.open(
        this.translateService.instant('forms.list.messages.linkCopied')
      );
    });
  }
  /**
   * Abre el formulario en modo lectura al hacer click en una fila
   */
  protected onFormRowClick(form: any): void {
    const dialogData: FormResponseData = {
      title: this.translateService.instant('forms.form.viewTitle'),
      formName: form.name,
      schema: form.schema || {},
      mode: 'view',
      formId: form.id,
    };

    this.dialog.open(FormResponseDialogComponent, {
      data: dialogData,
      panelClass: ['dialog-xl', 'dialog-safe-area', 'dialog-flex'],
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      disableClose: true,
    });
  }

  private editForm(form: any): void {
    // Abrir diálogo de edición de form
    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.editTitle'),
        mode: 'edit',
        form: form,
        context: 'beneficiary',
        partnerId: this.getCurrentPartnerId(),
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'edit' && result.formId) {
        this.handleFormUpdate(result.formId, result.payload);
      }
    });
  }

  private publishFormToPartner(form: any): void {
    const partnerId = form.partnerId || this.getCurrentPartnerId();

    if (!partnerId) {
      this.snackbar.error('common.errors.noPartnerContext');
      return;
    }

    if (form.status === 'DRAFT') {
      // DRAFT → finalize primero, luego publish
      this.formService.finalize(form.id).subscribe({
        next: () => {
          this.formService.publishToPartner(partnerId, form.id, 30).subscribe({
            next: () => {
              this.snackbar.open(
                this.translateService.instant('forms.list.messages.finalizedAndPublished')
              );
              this.loadForms();
            },
            error: (err) => {
              this.snackbar.notifyHttpError(err);
              this.loadForms(); // Recargar igualmente porque finalize sí fue exitoso
            },
          });
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    } else {
      // FINAL → solo publish
      this.formService.publishToPartner(partnerId, form.id, 30).subscribe({
        next: () => {
          this.snackbar.open(
            this.translateService.instant('forms.list.messages.publishedToPartner')
          );
          this.loadForms();
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    }
  }

  private getCurrentPartnerId(): string | undefined {
    // Retornar el partnerId guardado
    return this.currentPartnerId;
  }

  private deleteForm(form: any): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: { itemName: form.name },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.formService.delete(form.id).subscribe({
        next: () => {
          this.loadForms(); // Recargar la lista de forms
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    });
  }

  private handleUserUpdate(userId: string, userData: UserUpdate, newPassword?: string): void {
    // Primero actualizar los datos del usuario
    this.userService.update(userId, userData).subscribe({
      next: (updatedUser) => {
        // Si se proporcionó una nueva contraseña, cambiarla por separado
        if (newPassword && newPassword.trim() !== '') {
          this.userService.changePassword(userId, { newPassword }).subscribe({
            next: (userWithNewPassword) => {
              this.loadUsers(); // Recargar tabla al final
            },
            error: (error) => {
              // Aunque falle el cambio de contraseña, los datos del usuario ya se actualizaron
              this.snackbar.notifyHttpError(error);
              this.loadUsers();
            },
          });
        } else {
          // No se cambió la contraseña, solo recargar la tabla
          this.loadUsers();
        }
      },
      error: (error) => this.snackbar.notifyHttpError(error),
    });
  }

  protected onExportAllBeneficiaries(event: { selectedKeys: string[] }): void {
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const partnerId = params.get('partnerId');

      const exportParams: BeneficiaryListParams = {};
      if (countryId) exportParams.countryId = countryId;
      if (partnerId) exportParams.partnerId = partnerId;
      if (this.beneficiaryFilter) exportParams.filter = this.beneficiaryFilter;

      this.beneficiaryService.getAllForExport(exportParams).subscribe({
        next: (allBeneficiaries: Beneficiary[]) => {
          const exportData = allBeneficiaries.map((beneficiary: Beneficiary) => {
            const t = this.beneficiaryService.transformBeneficiaryData(beneficiary);
            return {
              id: t.id,
              firstName: t.firstName,
              lastName: t.lastName,
              email: t.email,
              phone: t.phone,
              gender: t.gender
                ? this.translateService.instant('beneficiaries.form.gender.options.' + t.gender)
                : '',
              birthDate: t.birthDate,
              hostCountry: t.hostCountry,
              birthCountry: t.birthCountry,
              externalId: t.externalId || '',
              createdAt: formatDate(beneficiary.createdAt),
              updatedAt: formatDate(beneficiary.updatedAt),
            };
          });

          const beneficiariesTable = this.tables.toArray()[0];
          if (beneficiariesTable) {
            beneficiariesTable.exportFilteredDataToExcel(exportData, event.selectedKeys);
          }
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    });
  }

  /**
   * Exportar todos los usuarios (no solo los paginados)
   * Usa el endpoint con filtro partnerId del contexto actual
   */
  protected onExportAllUsers(): void {
    this.route.paramMap.subscribe((params) => {
      const partnerId = params.get('partnerId');

      if (!partnerId) {
        this.snackbar.error('common.errors.missingPartnerId');
        return;
      }

      this.userService
        .getAllForExport({
          partnerId: partnerId,
        })
        .subscribe({
          next: (allUsers) => {
            // Mapear al formato de la tabla
            const exportData = allUsers.map((user) => ({
              id: user.id,
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              enabled: user.status === 'ACTIVE' ? '✓' : '',
              status: user.status,
              createdAt: formatDate(user.createdAt),
              updatedAt: formatDate(user.updatedAt),
            }));

            // Exportar usando la tercera tabla (usuarios)
            const usersTable = this.tables.toArray()[2];
            if (usersTable) {
              usersTable.exportAllDataToExcel(exportData);
            }
          },
          error: (err) => this.snackbar.notifyHttpError(err),
        });
    });
  }

  protected onExportAllForms(event: { selectedKeys: string[] }): void {
    this.route.paramMap.subscribe((params) => {
      const partnerId = params.get('partnerId');

      if (!partnerId) {
        this.snackbar.error('common.errors.missingPartnerId');
        return;
      }

      this.formService.getAllForExportInPartner(partnerId, {}).subscribe({
        next: (allForms) => {
          const exportData = allForms.map((form) => ({
            id: form.id,
            name: form.name,
            published: form.status === 'FINAL' ? '✓' : '',
            createdAt: formatDate(form.createdAt),
            updatedAt: formatDate(form.updatedAt),
            beneficiaries: (form as any).publication?.submissions?.total ?? 0,
          }));

          const formsTable = this.tables.toArray()[1];
          if (formsTable) {
            formsTable.exportFilteredDataToExcel(exportData, event.selectedKeys);
          }
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    });
  }

  private handleFormUpdate(formId: string, payload: any): void {
    this.formService.update(formId, payload).subscribe({
      next: () => {
        this.loadForms();
      },
      error: (error) => this.snackbar.notifyHttpError(error),
    });
  }
}
